import React from 'react';
import { ArrowDownLeft, Calendar } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface SalesTeamPerformanceReportProps {
  startDate: string;
  setStartDate: (date: string) => void;
  endDate: string;
  setEndDate: (date: string) => void;
  users: any[];
  invoices: any[];
  bills: any[];
  setActiveReport: (report: string | null) => void;
}

export const SalesTeamPerformanceReport: React.FC<SalesTeamPerformanceReportProps> = ({
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  users,
  invoices,
  bills,
  setActiveReport
}) => {
  const isWithinRange = (date: string) => {
    return date >= startDate && date <= endDate;
  };

  const performanceData = users.map(u => {
    const userInvoices = invoices.filter(i => i.createdBy === u.uid && isWithinRange(i.date));
    const userBills = bills.filter(b => b.createdBy === u.uid && isWithinRange(b.date));
    const earned = userInvoices.reduce((acc, curr) => acc + (curr.totalAmount || 0), 0);
    const spent = userBills.reduce((acc, curr) => acc + (curr.totalAmount || 0), 0);
    return {
      name: u.displayName || u.email,
      earned,
      spent
    };
  });

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <button 
          onClick={() => setActiveReport(null)}
          className="flex items-center gap-2 text-sm font-bold hover:opacity-70 transition-opacity"
        >
          <ArrowDownLeft className="w-4 h-4 rotate-45" />
          <span>Back to Reports</span>
        </button>
        
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
            <Calendar className="w-4 h-4 opacity-50" />
            <input 
              type="date" 
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest"
            />
            <span className="opacity-20">to</span>
            <input 
              type="date" 
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest"
            />
          </div>
        </div>
      </div>

      <div className="bg-white/50 border border-[#141414] p-12 rounded-[40px] shadow-sm">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold tracking-tighter italic serif mb-2">Sales Team Performance</h1>
          <p className="text-sm uppercase tracking-[0.3em] font-bold opacity-40">
            From {new Date(startDate).toLocaleDateString()} to {new Date(endDate).toLocaleDateString()}
          </p>
        </div>

        <div className="h-96 mb-12 relative">
          <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1} debounce={50}>
            <BarChart data={performanceData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} strokeOpacity={0.1} />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fontSize: 12, fontWeight: 600}} />
              <YAxis axisLine={false} tickLine={false} tick={{fontSize: 12, fontWeight: 600}} />
              <Tooltip 
                contentStyle={{borderRadius: '16px', border: '1px solid #141414'}}
              />
              <Bar dataKey="earned" fill="#141414" name="Earned (Invoices)" radius={[4, 4, 0, 0]} />
              <Bar dataKey="spent" fill="var(--primary)" name="Spent (Bills)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-[#141414] rounded-[32px] overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b-2 border-[#141414] bg-[#141414]/5">
                <th className="p-6 text-[10px] uppercase font-bold opacity-40">User Name</th>
                <th className="p-6 text-[10px] uppercase font-bold opacity-40 text-right">Total Earned</th>
                <th className="p-6 text-[10px] uppercase font-bold opacity-40 text-right">Total Spent</th>
                <th className="p-6 text-[10px] uppercase font-bold opacity-40 text-right">Net Contribution</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#141414]/5">
              {performanceData.map((data, idx) => (
                <tr key={idx} className="hover:bg-[#141414]/5 transition-colors">
                  <td className="p-6 text-sm font-bold">{data.name}</td>
                  <td className="p-6 text-right font-mono text-sm">${data.earned.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td className="p-6 text-right font-mono text-sm text-red-500">-${data.spent.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td className="p-6 text-right font-mono text-sm font-bold">
                    ${(data.earned - data.spent).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
